import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Save, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "name", label: "Nume firmă", placeholder: "BRAGUS IDEA SRL" },
  { key: "cif", label: "CIF", placeholder: "RO37343423" },
  { key: "reg_com", label: "Nr. Reg. Comerțului", placeholder: "J40/12345/2017" },
  { key: "address", label: "Adresă sediu", placeholder: "Str. Exemplu nr. 1, București", multiline: true },
  { key: "iban", label: "IBAN", placeholder: "RO00 BANK 0000 0000 0000 0000" },
  { key: "phone", label: "Telefon", placeholder: "+40 700 000 000" },
  { key: "email", label: "Email", placeholder: "contact@firma.ro", type: "email" },
];

export default function Company() {
  const [data, setData] = useState({ name: "", cif: "", reg_com: "", address: "", iban: "", phone: "", email: "", logo_url: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/company`);
      setData((d) => ({ ...d, ...(r.data || {}) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcarea datelor companiei");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onField = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const onSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/company`, data);
      toast.success("Date companie salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvarea a eșuat");
    } finally {
      setSaving(false);
    }
  };

  const onUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data?.url || r.data?.path || "";
      if (!url) throw new Error("Upload-ul nu a întors un URL");
      setData((d) => ({ ...d, logo_url: url }));
      toast.success("Logo încărcat");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la upload logo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (loading) {
    return <div className="p-8 text-zinc-500" data-testid="company-loading">Se încarcă...</div>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6 md:p-8" data-testid="company-page">
      <header className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/15 text-orange-300">
          <Building2 className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">Companie</h1>
          <p className="text-xs text-zinc-500">Date legale ale firmei (folosite la facturi, AWB-uri, footer email)</p>
        </div>
      </header>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6">
        <div className="mb-6 flex items-center gap-4">
          <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950">
            {data.logo_url ? (
              <img src={data.logo_url.startsWith("http") ? data.logo_url : `${process.env.REACT_APP_BACKEND_URL}${data.logo_url}`} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="company-logo-preview" />
            ) : (
              <Building2 className="h-8 w-8 text-zinc-700" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-zinc-200">Logo firmă</p>
            <p className="mb-2 text-[11px] text-zinc-500">PNG sau JPG, transparent recomandat</p>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-xs text-zinc-200 hover:border-orange-500/40 hover:text-orange-200">
              <Upload className="h-3.5 w-3.5" />
              {uploading ? "Se încarcă..." : "Încarcă logo"}
              <input type="file" accept="image/*" className="hidden" onChange={onUploadLogo} disabled={uploading} data-testid="company-logo-input" />
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.multiline ? "md:col-span-2" : ""}>
              <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">{f.label}</label>
              {f.multiline ? (
                <textarea
                  data-testid={`company-${f.key}-input`}
                  value={data[f.key] || ""}
                  onChange={onField(f.key)}
                  placeholder={f.placeholder}
                  rows={2}
                  className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-orange-500/50 focus:outline-none"
                />
              ) : (
                <input
                  data-testid={`company-${f.key}-input`}
                  type={f.type || "text"}
                  value={data[f.key] || ""}
                  onChange={onField(f.key)}
                  placeholder={f.placeholder}
                  className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-orange-500/50 focus:outline-none"
                />
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            data-testid="company-save-btn"
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-rose-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 hover:from-orange-400 hover:to-rose-400 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? "Se salvează..." : "Salvează"}
          </button>
        </div>
      </div>
    </div>
  );
}
